<script setup lang="ts">
    import { onMounted, reactive, computed } from 'vue';
    import { useRouter } from 'vue-router';
    import {
        getTemplateDataSources,
        getAccountDataSourceListItems,
        addAccountToDataSource,
        removeAccountFromDataSource,
        addAllSiblingAccountsToDataSource,
        type AccountListItem,
        type DataSourceListItem,
        type AccountDataSourceListItem,
    } from './DataSource.access';
    import { getAccount, getClientCompanyAccounts, type Account } from '@cwd/Views/BackOffice/Account/Account.access';
    import type { AccountListItem as SubscriptionAccountListItem } from '@cwd/Views/BackOffice/Account/AccountList.access';
    import List from '@shared/components/List/List.vue';
    import SingleSelect from '@shared/components/SingleSelect.vue';
    import Modal from '@shared/components/Modal.vue';
    import NavIcons from '@cwd/Navigation/NavIcons.vue';
    import Loading from '@shared/components/Loading.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';

    const props = defineProps<{
        accountId: string;
    }>();

    const router = useRouter();

    const state = reactive<{
        templateDataSources: DataSourceListItem[];
        accountDataSources: AccountDataSourceListItem[];
        accountIdsAndLinkedDataSources: Record<string, string[]>;
        dataSourcesAndLinkedAccounts: Record<string, AccountListItem[]>;
        selectedAccount: Account | null;
        selectedDataSourceId: string | null;
        checkedDataSourceIds: string[];
        isLoading: boolean;
        isAddingAccount: boolean;
        isApplyingToSubscription: boolean;
        isRemovingFromAccount: boolean;
        showLinkedAccountsModal: boolean;
        linkedAccounts: AccountListItem[];
        linkedAccountsDataSourceName: string;
        showAddToSubscriptionModal: boolean;
        subscriptionAccounts: SubscriptionAccountListItem[];
        isLoadingSubscriptionAccounts: boolean;
    }>({
        templateDataSources: [],
        accountDataSources: [],
        accountIdsAndLinkedDataSources: {},
        dataSourcesAndLinkedAccounts: {},
        selectedAccount: null,
        selectedDataSourceId: null,
        checkedDataSourceIds: [],
        isLoading: false,
        isAddingAccount: false,
        isApplyingToSubscription: false,
        isRemovingFromAccount: false,
        showLinkedAccountsModal: false,
        linkedAccounts: [],
        linkedAccountsDataSourceName: '',
        showAddToSubscriptionModal: false,
        subscriptionAccounts: [],
        isLoadingSubscriptionAccounts: false,
    });

    const availableDataSourcesToAdd = computed(() => {
        return state.templateDataSources.filter((t) => !t.IsGlobal);
    });

    const currentAccountDataSources = computed(() => {
        const dataSourceIds = state.accountIdsAndLinkedDataSources[props.accountId] ?? [];
        return dataSourceIds.map((dataSourceId) => {
            const template = state.templateDataSources.find((t) => t.DataSourceId === dataSourceId);
            return { DataSourceId: dataSourceId, Name: template?.Name };
        });
    });

    async function loadDataSources(): Promise<void> {
        if (state.isLoading) return;
        state.isLoading = true;
        try {
            state.templateDataSources = await getTemplateDataSources();
            state.accountDataSources = await getAccountDataSourceListItems();

            state.accountIdsAndLinkedDataSources = {};
            state.dataSourcesAndLinkedAccounts = {};
            for (const adsListItem of state.accountDataSources) {
                if (!state.accountIdsAndLinkedDataSources[adsListItem.AccountId]) {
                    state.accountIdsAndLinkedDataSources[adsListItem.AccountId] = [];
                }
                state.accountIdsAndLinkedDataSources[adsListItem.AccountId].push(adsListItem.DataSourceId);

                if (!state.dataSourcesAndLinkedAccounts[adsListItem.DataSourceId]) {
                    state.dataSourcesAndLinkedAccounts[adsListItem.DataSourceId] = [];
                }
                state.dataSourcesAndLinkedAccounts[adsListItem.DataSourceId].push(adsListItem);
            }
        } catch (error) {
            bsAlert({
                title: 'Error',
                content: 'Failed to load data sources',
                type: AlertTypes.Danger,
            });
        } finally {
            state.isLoading = false;
        }
    }

    async function loadSelectedAccount(): Promise<void> {
        try {
            state.selectedAccount = state.selectedAccount ?? (await getAccount(props.accountId));
        } catch (error) {
            bsAlert({
                title: 'Error',
                content: 'Failed to load account information',
                type: AlertTypes.Danger,
            });
        }
    }

    async function addToAccount(): Promise<void> {
        if (!state.selectedDataSourceId || state.isAddingAccount) return;
        state.isAddingAccount = true;
        try {
            await addAccountToDataSource(state.selectedDataSourceId, props.accountId);
            state.selectedDataSourceId = null;
            await loadDataSources();
            bsAlert({
                title: 'Success',
                content: 'Account added to data source',
            });
        } catch (error) {
            bsAlert({
                title: 'Error',
                content: 'Failed to add account to data source',
                type: AlertTypes.Danger,
            });
        } finally {
            state.isAddingAccount = false;
        }
    }

    async function addToSubscription(): Promise<void> {
        if (!state.selectedDataSourceId || state.isApplyingToSubscription) return;
        state.isApplyingToSubscription = true;
        try {
            await addAllSiblingAccountsToDataSource(state.selectedDataSourceId, props.accountId);
            await loadDataSources();
            bsAlert({
                title: 'Success',
                content: 'All accounts in subscription added to data source',
            });
        } catch (error) {
            bsAlert({
                title: 'Error',
                content: 'Failed to add all accounts in subscription to data source',
                type: AlertTypes.Danger,
            });
        } finally {
            state.selectedDataSourceId = null;
            state.isApplyingToSubscription = false;
        }
    }

    async function removeSelectedFromAccount(): Promise<void> {
        if (state.checkedDataSourceIds.length === 0 || state.isRemovingFromAccount) return;

        state.isRemovingFromAccount = true;

        try {
            for (const dataSourceId of state.checkedDataSourceIds) {
                await removeAccountFromDataSource(dataSourceId, props.accountId);
            }
            state.checkedDataSourceIds = [];
            await loadDataSources();
            state.selectedDataSourceId = null;
            bsAlert({
                title: 'Success',
                content: 'Data source(s) removed from account',
            });
        } catch (error) {
            bsAlert({
                title: 'Error',
                content: 'Failed to remove data source(s) from account',
                type: AlertTypes.Danger,
            });
        } finally {
            state.isRemovingFromAccount = false;
        }
    }

    const readonlyDataSources = computed(() => {
        return state.templateDataSources.filter((ds) => ds.AccountId === state.selectedAccount?.AccountId).map((ds) => ds.DataSourceId);
    });

    const selectedDataSourceName = computed(() => {
        if (!state.selectedDataSourceId) return '';
        return state.templateDataSources.find((t) => t.DataSourceId === state.selectedDataSourceId)?.Name ?? '';
    });

    const btnAddToAccountDisabled = computed(() => {
        const linkedIds = state.accountIdsAndLinkedDataSources[state.selectedAccount?.AccountId ?? ''] ?? [];
        return !state.selectedDataSourceId || state.isAddingAccount || linkedIds.some((dataSourceId: string) => dataSourceId === state.selectedDataSourceId);
    });

    function accountHasDataSource(accountId: string): boolean {
        return (state.accountIdsAndLinkedDataSources[accountId] ?? []).includes(state.selectedDataSourceId ?? '');
    }

    const allSubscriptionAccountsHaveDataSource = computed(
        () => state.subscriptionAccounts.length > 0 && state.subscriptionAccounts.every((a) => accountHasDataSource(a.AccountId))
    );

    /** Other accounts in same client company linked to this data source (excludes the selected account). */
    function getSiblingAccountsForDataSource(dataSourceId: string): AccountListItem[] {
        const companyId = state.selectedAccount?.ClientCompany?.ClientCompanyId;
        if (!companyId) return [];
        var linkedAccounts = state.dataSourcesAndLinkedAccounts[dataSourceId];
        return linkedAccounts.filter((a) => a.ClientCompanyId === companyId && a.AccountId !== state.selectedAccount?.AccountId);
    }

    function openLinkedAccountsModal(item: { DataSourceId: string; Name?: string }): void {
        state.linkedAccounts = getSiblingAccountsForDataSource(item.DataSourceId);
        state.linkedAccountsDataSourceName = item.Name ?? state.templateDataSources.find((t) => t.DataSourceId === item.DataSourceId)?.Name ?? '';
        state.showLinkedAccountsModal = true;
    }

    function closeLinkedAccountsModal(): void {
        state.showLinkedAccountsModal = false;
        state.linkedAccounts = [];
    }

    async function openAddToSubscriptionModal(): Promise<void> {
        const companyId = state.selectedAccount?.ClientCompany?.ClientCompanyId;
        if (!companyId || !state.selectedDataSourceId) return;
        state.showAddToSubscriptionModal = true;
        state.subscriptionAccounts = [];
        state.isLoadingSubscriptionAccounts = true;
        try {
            const accounts = await getClientCompanyAccounts(companyId);
            state.subscriptionAccounts = [...accounts].sort((a, b) => (a.Name ?? '').localeCompare(b.Name ?? '', undefined, { sensitivity: 'base' }));
        } catch (error) {
            bsAlert({
                title: 'Error',
                content: 'Failed to load accounts in subscription',
                type: AlertTypes.Danger,
            });
            state.showAddToSubscriptionModal = false;
        } finally {
            state.isLoadingSubscriptionAccounts = false;
        }
    }

    function closeAddToSubscriptionModal(): void {
        state.showAddToSubscriptionModal = false;
        state.subscriptionAccounts = [];
    }

    function switchToAccountFromSubscriptionModal(account: { AccountId: string }): void {
        closeAddToSubscriptionModal();
        router.push({ name: 'AccountEdit', params: { accountId: account.AccountId } });
    }

    async function confirmAddToSubscription(): Promise<void> {
        await addToSubscription();
        closeAddToSubscriptionModal();
    }

    function switchToAccount(account: AccountListItem): void {
        closeLinkedAccountsModal();
        router.push({ name: 'AccountEdit', params: { accountId: account.AccountId } });
    }

    onMounted(async () => {
        await Promise.all([loadDataSources(), loadSelectedAccount()]);
    });
</script>

<template>
    <div>
        <Loading :show="state.isLoading" loadingStyle="progress-bar" :fixed="true" />

        <BsAlert
            v-if="!state.isLoading && state.templateDataSources.length === 0"
            :type="AlertTypes.Info"
            :dismissible="false"
            title="No data sources found"
            content="No template data sources are available. You can only add template data sources to accounts." />

        <template v-if="!state.isLoading && state.templateDataSources.length > 0">
            <div class="form-group d-flex gap-sm align-items-end flex-wrap max-900">
                <div class="flex-1" style="min-width: 300px">
                    <SingleSelect
                        v-model="state.selectedDataSourceId"
                        :items="availableDataSourcesToAdd"
                        selectProp="DataSourceId"
                        :displayProps="['Name']"
                        placeholder="Select data source to add..."
                        label="" />
                </div>
                <div class="d-flex gap-sm">
                    <button class="btn btn-primary" :class="{ 'is-busy': state.isAddingAccount }" :disabled="btnAddToAccountDisabled" @click="addToAccount">
                        Add to account
                    </button>
                    <button
                        class="btn btn-default"
                        :class="{ 'is-busy': state.isApplyingToSubscription }"
                        :disabled="!state.selectedDataSourceId || state.isApplyingToSubscription"
                        @click="openAddToSubscriptionModal">
                        Add to all accounts in subscription
                    </button>
                </div>
            </div>

            <List
                v-if="currentAccountDataSources.length > 0"
                :items="currentAccountDataSources"
                itemId="DataSourceId"
                displayAs="table"
                :tableHeaders="['DataSource', 'Linked Accounts']"
                :orderOptions="[{ key: 'Name', display: 'Name' }]"
                orderByDirection="asc"
                :checkedItems="state.checkedDataSourceIds"
                :disabledCheckboxes="readonlyDataSources"
                :loading="state.isLoading"
                :deleting="state.isRemovingFromAccount"
                :deleteConfirmation="true"
                :filterKeys="['Name']"
                title=""
                description="Data sources linked to this account"
                @update:checkedItems="(ids) => (state.checkedDataSourceIds = (ids ?? []).map(String))"
                @deleteSelected="removeSelectedFromAccount"
                @clearSelected="state.checkedDataSourceIds = []">
                <template #item="{ item }">
                    <td>{{ item.Name }}</td>
                    <td>
                        <template v-if="getSiblingAccountsForDataSource(item.DataSourceId).length > 0">
                            <a href="#" @click.prevent="openLinkedAccountsModal(item)">
                                View Account(s) Details ({{ getSiblingAccountsForDataSource(item.DataSourceId).length }})
                            </a>
                        </template>
                        <span v-else>—</span>
                    </td>
                </template>
            </List>

            <BsAlert v-else :type="AlertTypes.Info" :dismissible="false" content="No data sources linked to this account." />
        </template>

        <Modal
            :title="'Linked Accounts' + (state.linkedAccountsDataSourceName ? ' – ' + state.linkedAccountsDataSourceName : '')"
            :show="state.showLinkedAccountsModal"
            @close="closeLinkedAccountsModal"
            size="md">
            <template #content>
                <p class="m-lg-b">Accounts in the same subscription linked to this data source. Click an account to switch view.</p>
                <List :items="state.linkedAccounts" itemId="AccountId" displayAs="table" :tableHeaders="['Account', 'Client Company']" hideHeader hideToolbar>
                    <template #item="{ item }">
                        <td>
                            <a v-if="(item.AccountId || '').toString() !== (props.accountId || '').toString()" href="#" @click.prevent="switchToAccount(item)">
                                {{ item.AccountName }}
                            </a>
                            <span v-else>{{ item.AccountName }}</span>
                        </td>
                        <td>{{ item.ClientCompanyName ?? '—' }}</td>
                    </template>
                </List>
            </template>
        </Modal>

        <Modal
            :title="'Add to all accounts in subscription' + (selectedDataSourceName ? ' – ' + selectedDataSourceName : '')"
            :show="state.showAddToSubscriptionModal"
            @close="closeAddToSubscriptionModal"
            size="md">
            <template #content>
                <p class="m-lg-b">Accounts in the same subscription. Click an account to switch view. Confirm below to add the data source to all.</p>
                <Loading :show="state.isLoadingSubscriptionAccounts" loadingStyle="progress-bar" :fixed="true" />
                <List
                    v-if="!state.isLoadingSubscriptionAccounts && state.subscriptionAccounts.length > 0"
                    :items="state.subscriptionAccounts"
                    itemId="AccountId"
                    displayAs="table"
                    :tableHeaders="['Account', 'Data Source Added']"
                    hideHeader
                    hideToolbar>
                    <template #item="{ item }">
                        <td>
                            <a
                                v-if="(item.AccountId || '').toString() !== (props.accountId || '').toString()"
                                href="#"
                                @click.prevent="switchToAccountFromSubscriptionModal(item)">
                                {{ item.Name }}
                            </a>
                            <span v-else>{{ item.Name }}</span>
                        </td>
                        <td>
                            <span v-if="accountHasDataSource(item.AccountId)" title="Already added">
                                <NavIcons icon="check" :size="14" :strokeWidth="3" />
                            </span>
                        </td>
                    </template>
                </List>
                <p v-else-if="!state.isLoadingSubscriptionAccounts" class="text-muted m-0">No accounts in this subscription.</p>
            </template>
            <template #button>
                <button
                    type="button"
                    class="btn btn-primary"
                    :class="{ 'is-busy': state.isApplyingToSubscription }"
                    :disabled="state.isApplyingToSubscription || allSubscriptionAccountsHaveDataSource"
                    @click="confirmAddToSubscription">
                    Add to all accounts
                </button>
            </template>
        </Modal>
    </div>
</template>
